import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { palette5, tokens } from "../theme";
import { Card, Cards, Eyebrow, HeroPanel, Section, heroButtonSx } from "../components/ui";
import { FALLBACK_CARE_CATEGORIES } from "../data/site";
import { getTreatmentCategories } from "../services/veysoApi";
import { useApiData } from "../hooks/useApiData";

const HUBS = {
  women: {
    title: "Women's Hub", theme: "gold", line: "Personalized products, testing, and care for women.", tag: "Women's health. Built for what's next.",
    tint: palette5.oxide[4], accent: palette5.oxide[0],
  },
  men: {
    title: "Men's Hub", theme: "navy", line: "Personalized products, testing, and care for men.", tag: "Men's health. Built for higher performance.",
    tint: palette5.navy[4], accent: palette5.navy[0],
  },
};

export default function Hub({ audience }) {
  const hub = HUBS[audience];
  const { data } = useApiData(() => getTreatmentCategories(audience), FALLBACK_CARE_CATEGORIES.filter((c) => c.audience === audience), { deps: [audience] });
  const who = audience === "women" ? "Women's" : "Men's";
  const careLinks = audience === "women"
    ? ["Women's health overview", "Areas of focus", "Peptides", "Hormone therapy", "How it works", "Meet your providers", "Pricing", "FAQ", "Start consultation"]
    : ["Men's health overview", "Areas of focus", "Peptides", "Testosterone therapy", "How it works", "Meet your providers", "Pricing", "FAQ", "Start consultation"];
  const careTo = (l) => ({ Peptides: "/get-care/info/peptides", "Hormone therapy": "/get-care/info/hormone-therapy", "Testosterone therapy": "/get-care/info/hormone-therapy",
    "How it works": "/how-it-works", "Meet your providers": "/get-care/info/providers", Pricing: "/membership", FAQ: "/faqs", "Start consultation": `/get-care/${audience}` }[l] || `/get-care/${audience}`);
  const panels = [
    { title: `Shop`, sub: `${who} products`, accent: palette5.oxide[0], tint: palette5.oxide[4],
      links: [["Browse by goal", `/shop/${audience}`], ["Featured products", `/shop/${audience}`], ["All products", "/shop"], ["Product detail pages", `/shop/${audience}`], ["Bundles / stacks", `/shop/${audience}`], ["Why we picked it", "/about"], ["Reviews", `/shop/${audience}`], ["FAQ", "/faqs"]],
      cta: [`Shop ${who}`, `/shop/${audience}`] },
    { title: "Testing", sub: "Labs & insights", accent: palette5.moss[0], tint: palette5.moss[4],
      links: [["All testing options", "/testing"], ["DNA testing", "/testing/dna"], ["Hormone testing", "/testing/hormone"], ["Blood testing / biomarkers", "/testing/blood"], ["Epigenetic testing", "/testing/epigenetic"], ["Gut / microbiome", "/testing/gut"], ["Compare tests", "/testing/compare"], ["Sample reports (preview)", "/testing/sample-reports"], ["FAQ", "/faqs"]],
      cta: ["Shop tests", "/testing"] },
    { title: "Get Care", sub: `${who} telehealth`, accent: palette5.navy[0], tint: palette5.navy[4],
      links: careLinks.map((l) => [l, careTo(l)]), cta: [`Get ${who} care`, `/get-care/${audience}`] },
  ];

  return (
    <>
      <HeroPanel
        theme={hub.theme} eyebrow={hub.tag} title={hub.title} subtitle={hub.line}
        actions={<>
          <Button size="large" component={RouterLink} to={`/get-care/${audience}`} sx={heroButtonSx(hub.theme)}>Get {who} care</Button>
          <Button size="large" component={RouterLink} to="/testing" sx={heroButtonSx(hub.theme, "ghost")}>Explore testing</Button>
        </>}
        minHeight={480}
      />
      <Section>
        <Eyebrow>Areas of focus</Eyebrow>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          {(data || []).map((c) => (
            <Box key={c.slug} component={RouterLink} to={`/treatment/${c.slug}-visit`} sx={{ textDecoration: "none", color: tokens.ink, background: tokens.paper, border: `1px solid ${tokens.line}`, borderRadius: 999, px: 2.5, py: 1.1, "&:hover": { background: hub.tint } }}>{c.name}</Box>
          ))}
        </Box>
      </Section>
      <Section tone="white" sx={{ pt: { xs: 5, md: 6 } }}>
        <Cards min={290}>
          {panels.map((p) => (
            <Card key={p.title} sx={{ p: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <Box sx={{ background: p.tint, p: 3 }}>
                <Typography variant="h4" sx={{ fontSize: 26 }}>{p.title}</Typography>
                <Typography sx={{ color: tokens.muted, fontSize: 14.5 }}>{p.sub}</Typography>
              </Box>
              <Box sx={{ p: 3, flex: 1 }}>
                {p.links.map(([l, to]) => (
                  <Typography key={l} component={RouterLink} to={to} sx={{ display: "block", color: tokens.ink, textDecoration: "none", py: 0.55, fontSize: 15, "&:hover": { color: p.accent, textDecoration: "underline" } }}>{l}</Typography>
                ))}
              </Box>
              <Box sx={{ p: 3, pt: 0 }}>
                <Button fullWidth variant="contained" component={RouterLink} to={p.cta[1]} sx={{ background: p.accent, "&:hover": { background: p.accent, filter: "brightness(.9)" } }}>{p.cta[0]}</Button>
              </Box>
            </Card>
          ))}
        </Cards>
      </Section>
    </>
  );
}

